import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  useAnalyzeGsCheckMutation,
  useCreateGsCheckMutation,
  useCreateSmetaRequestMutation,
  useDeleteGsCheckMutation,
  useGetGsCheckQuery,
  useLazyExportGsCheckXlsxQuery,
  useListGsChecksQuery,
} from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { linkKindLabel, statusLabel, statusTone } from './helpers';
import { CheckRow, Grid, Muted, ObjName, Row, Stack } from './styles';
import { ParseWarningsBanner } from '../ParseWarningsBanner';
import { UploadCheckCard } from './components/UploadCheckCard';
import { CheckDetailCard } from './components/CheckDetailCard';

export function GsChecks() {
  const { activeProjectName } = useActiveProjectContext();
  const [objectName, setObjectName] = useState(activeProjectName ?? '');
  const [region, setRegion] = useState('');
  const [gpFiles, setGpFiles] = useState<FileList | null>(null);
  const [spFiles, setSpFiles] = useState<FileList | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadWarnings, setUploadWarnings] = useState<{
    checkId: string;
    warnings: string[];
  } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [poll, setPoll] = useState(0);
  const [exportError, setExportError] = useState<string | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);

  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  useEffect(() => {
    if (activeProjectName && !objectName) {
      setObjectName(activeProjectName);
    }
  }, [activeProjectName, objectName]);

  const listQ = useListGsChecksQuery(undefined, { pollingInterval: 5000 });
  const [createCheck, { isLoading: uploading }] = useCreateGsCheckMutation();
  const [deleteCheck] = useDeleteGsCheckMutation();
  const [analyze, { isLoading: analyzing }] = useAnalyzeGsCheckMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const detailQ = useGetGsCheckQuery(selectedId ?? '', { skip: !selectedId, pollingInterval: poll });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportGsCheckXlsxQuery();

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.check.status;
    setPoll(st === 'pending' || st === 'parsing' || st === 'analyzing' ? 2500 : 0);
  }, [selectedId, detailQ.data?.check.status]);

  async function handleUpload(e: FormEvent) {
    e.preventDefault();
    setUploadError(null);
    setUploadWarnings(null);
    if (!gpFiles || gpFiles.length === 0) {
      setUploadError('Приложите хотя бы один файл стороны генподряда.');
      return;
    }
    if (!spFiles || spFiles.length === 0) {
      setUploadError('Приложите хотя бы один файл стороны субподряда.');
      return;
    }
    const fd = new FormData();
    if (objectName.trim()) fd.append('object_name', objectName.trim());
    if (region.trim()) fd.append('region', region.trim());
    Array.from(gpFiles).forEach((f) => fd.append('gp', f));
    Array.from(spFiles).forEach((f) => fd.append('sp', f));
    try {
      const res = await createCheck(fd).unwrap();
      setUploadWarnings(
        res.warnings?.length ? { checkId: res.check_id, warnings: res.warnings } : null,
      );
      setGpFiles(null);
      setSpFiles(null);
      setObjectName('');
      setSelectedId(res.check_id);
    } catch (err) {
      setUploadError('Не удалось загрузить пакеты. Проверьте формат файлов (xlsx).');
      // eslint-disable-next-line no-console
      console.error('createGsCheck failed', err);
    }
  }

  async function handleAnalyze(id: string) {
    if (
      detail?.check.id === id &&
      detail.check.status === 'analyzed' &&
      !window.confirm(
        'Повторная сверка удалит все ваши решения по расхождениям (принято / спорно / отклонено). Продолжить?',
      )
    ) {
      return;
    }
    try {
      await analyze(id).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('analyzeGsCheck failed', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteCheck(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch {
      // список обновится при поллинге
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gs_check_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить отчёт сверки.');
      // eslint-disable-next-line no-console
      console.error('exportGsCheckXlsx failed', err);
    }
  }

  async function handleSendDsRequest() {
    if (!detail) return;
    setRequestNotice(null);
    setExportError(null);
    try {
      await createRequest({
        kind: 'contract_price_revision',
        contract_id: detail.check.contract_id ?? undefined,
        project_id: detail.check.project_id ?? undefined,
        title: `Подготовка ДС по сверке ГП↔СП: ${detail.check.object_name}`,
        summary: `Сметно-ценовой отдел провёл сверку генподряд↔субподряд по объекту «${detail.check.object_name}» (расхождений: ${detail.discrepancies.length}). Требуется пересмотр договорных позиций / ДС.`,
        details: {
          gs_check_id: detail.check.id,
          object_name: detail.check.object_name,
          contract_ref: detail.check.contract_ref,
          discrepancy_count: detail.discrepancies.length,
        },
      }).unwrap();
      setRequestNotice('Заявка на подготовку ДС отправлена в Юриста (Статут) / Экономиста.');
    } catch {
      setExportError('Не удалось отправить заявку на ДС.');
    }
  }

  const checks = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const isAuthor =
    !detail?.check.user_id ||
    !currentUserId ||
    detail.check.user_id === currentUserId;
  const canAnalyze =
    !!detail && ['parsed', 'analyzed', 'failed'].includes(detail.check.status);

  return (
    <Stack>
      <UploadCheckCard
        objectName={objectName}
        setObjectName={setObjectName}
        region={region}
        setRegion={setRegion}
        setGpFiles={setGpFiles}
        setSpFiles={setSpFiles}
        uploading={uploading}
        can_write={can_write}
        uploadError={uploadError}
        onUpload={handleUpload}
      />

      <Grid>
        <Stack>
          <Muted>Сверок: {checks.length}</Muted>
          {checks.map((c) => (
            <CheckRow key={c.id} $active={c.id === selectedId} onClick={() => setSelectedId(c.id)}>
              <Row>
                <ObjName>{c.object_name}</ObjName>
                <Badge $tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
              </Row>
              <Row>
                <Muted>
                  {linkKindLabel(c.link_kind)} · ГП {c.gp_position_count} / СП {c.sp_position_count}
                  {' · '}
                  {new Date(c.created_at).toLocaleDateString('ru-RU')}
                </Muted>
              </Row>
            </CheckRow>
          ))}
          {checks.length === 0 && (
            <Muted>Пока нет сверок. Загрузите пакеты обеих сторон выше.</Muted>
          )}
        </Stack>

        <div>
          {!selectedId && <Muted>Выберите сверку слева, чтобы увидеть маржу и расхождения.</Muted>}
          {selectedId && !detail && <Muted>Загрузка…</Muted>}
          {detail && (
            <Stack>
              <ParseWarningsBanner
                warnings={
                  detail.warnings?.length
                    ? detail.warnings
                    : uploadWarnings?.checkId === detail.check.id
                      ? uploadWarnings.warnings
                      : []
                }
              />
              <CheckDetailCard
              detail={detail}
              canAnalyze={canAnalyze}
              analyzing={analyzing}
              can_write={can_write}
              exporting={exporting}
              sendingRequest={sendingRequest}
              isAuthor={isAuthor}
              requestNotice={requestNotice}
              exportError={exportError}
              onAnalyze={handleAnalyze}
              onExport={handleExport}
              onSendDsRequest={handleSendDsRequest}
              onDelete={handleDelete}
              />
            </Stack>
          )}
        </div>
      </Grid>
    </Stack>
  );
}

export default GsChecks;
